import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import ExistingEmailModal from 'components/Modals/ExistingEmailModal'
import InvestedEmailModal from 'components/Modals/InvestedEmailModal'
import useFormProvider from 'hooks/useFormContext'
import { useGTMData } from 'hooks/useGTMData'
import { useUTMTracking } from 'hooks/useUTMTracking'
import queryString from 'query-string'
import React, { ChangeEvent, useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { useLocation, useNavigate } from 'react-router-dom'
import { getModifiedUrlWithStep } from 'utils'
import { consoleLog } from 'utils/simpleLogger'
import CompanyLogo from '../../assets/afbc.png'
import Side1 from '../../assets/new_side_1.jpg'
import Button from '../../components/Button'
import ErrorMessage from '../../components/ErrorMessage'
import FormLabel from '../../components/FormLabel'
import InputField from '../../components/InputField'
import LoaderModal from '../../components/Modals/LoaderModal'
import MessageModal from '../../components/Modals/MessageModal'
import Stepper from '../../components/Stepper'
import { useRiseUpAPIV1 } from '../../hooks/useRiseUpAPIV1'
import { IFormProps } from '../../interfaces'

export enum EMAIL_ONBOARDING_STATUS {
  IS_CREATED_FROM_DIRECT_INVEST = 'IS_CREATED_FROM_DIRECT_INVEST',
  IS_FINISHED_ONBOARDING = 'IS_FINISHED_ONBOARDING',
  IS_UNFINISHED_ONBOARDING = 'IS_UNFINISHED_ONBOARDING',
  IS_AVAILABLE = 'IS_AVAILABLE',
}

const Email = (props: IFormProps) => {
  const [isLoading, setisLoading] = useState(false)
  const [showEmailVerifiedModal, setshowEmailVerifiedModal] = useState(false)
  const [showEmailConfirmationSentModal, setshowEmailConfirmationSentModal] = useState(false)
  const [showInvestedUserModal, setShowInvestedUserModal] = useState(false)
  const [showExistingEmailModal, setshowExistingEmailModal] = useState(false)
  const [showExpiredInvestorLink, setShowExpiredInvestorLink] = useState(false)
  const [lastCompletedStage, setlastCompletedStage] = useState('2')
  const [investedDescription, setInvestedDescription] = useState('')
  const [userHasSetPassword, setUserHasSetPassword] = useState(false)

  const { getOnboardProgress, sendOnboardingURL, checkEmailStatus, getUserStatus, sendSetPasswordEmail } =
    useRiseUpAPIV1()
  const location = useLocation()
  const sendDataToGTM = useGTMDispatch()
  const navigate = useNavigate()
  const { setEmail } = useFormProvider()
  const gtmData = useGTMData(props.getValues)
  const {
    getFormattedUTMParams, // Helper to generate a complete GTM event object
  } = useUTMTracking()

  /**
   * NOTE: Enable when investor dashboard is ready
   */
  const cultivateDashboardLogin =
    process.env.REACT_APP_ENV === 'prod'
      ? 'https://cultivate-admin-dashboard.vercel.app/auth/investor/sign-in'
      : 'https://cultivate-admin-dashboard-git-develop-lennyjellys-projects.vercel.app/auth/investor/sign-in'

  const onStartFromBeginning = async () => {
    setisLoading(true)
    try {
      const sendOnboardingURLRes = await sendOnboardingURL(props.getValues('email'))

      if (sendOnboardingURLRes.status === 201) {
        const onboardingUrl = sendOnboardingURLRes?.data?.url
        const onboardingId = onboardingUrl.split('=').pop()
        await fetchData(onboardingId, '')

        const email = props.getValues('email')
        const firstName = props.getValues('basicInfo.firstName')
        const lastName = props.getValues('basicInfo.lastName')

        props.reset()

        props.setValue('email', email)
        props.setValue('onboardingId', onboardingId)
        props.setValue('basicInfo.firstName', firstName)
        props.setValue('basicInfo.lastName', lastName)

        setisLoading(false)
        props.nextStep('2')
      }
      consoleLog('Onboarding URL response', sendOnboardingURLRes)
    } catch (error) {
      console.log(error)
    }
  }

  const continueToLastStop = (lastCompletedStage: string) => {
    setlastCompletedStage(String(lastCompletedStage))
    props.calcProgressState(String(lastCompletedStage))
    props.setFormState({ step: String(lastCompletedStage) })
    setisLoading(false)
  }

  const handleSubmit = async () => {
    setisLoading(true)
    props.validateFields(['email', 'basicInfo.firstName', 'basicInfo.lastName'])

    if (!props.isValid) {
      setisLoading(false)
      return
    }

    const formattedUTMParams = getFormattedUTMParams()
    const pathname = location.pathname
    const currentParams = queryString.parse(location.search)
    const updatedUrl = getModifiedUrlWithStep(pathname, currentParams, 'investor_type')

    try {
      const { data: emailStatus } = await checkEmailStatus(props.getValues('email'))
      if (emailStatus === EMAIL_ONBOARDING_STATUS.IS_AVAILABLE) {
        sendDataToGTM({
          event: 'begin_checkout',
          step_title: 'EMAIL',
          ...gtmData,
          ...formattedUTMParams,
        })
        props.setValue('fromSecondPage', false)
        navigate(updatedUrl)
        props.nextStep('2')
      } else if (emailStatus === EMAIL_ONBOARDING_STATUS.IS_UNFINISHED_ONBOARDING) {
        // Send them back to start instead of showing the modal
        // setshowExistingEmailModal(true)
        const sendOnboardingURLRes = await sendOnboardingURL(props.getValues('email'))

        const url = sendOnboardingURLRes.data.url
        const onboardingIdMatch = url.match(/[?&]onboardingId=([^&]+)/)

        // Return the captured group if found, otherwise null
        const id = onboardingIdMatch ? onboardingIdMatch[1] : null

        props.setValue('onboardingId', id)
        sendDataToGTM({
          event: 'begin_checkout',
          step_title: 'EMAIL',
          ...gtmData,
          ...formattedUTMParams,
        })
        props.setValue('fromSecondPage', false)
        navigate(updatedUrl)
        props.nextStep('2')
      } else if (emailStatus === EMAIL_ONBOARDING_STATUS.IS_FINISHED_ONBOARDING) {
        setEmail(props.getValues('email'))

        setInvestedDescription('You can now reinvest by clicking Continue')
        setShowInvestedUserModal(true)
        // const { data: userStatus } = await getUserStatus(props.getValues('email'))

        // if (userStatus.userHasSetPassword) {
        //   setInvestedDescription('You can now access your account in the Investor Dashboard')
        //   setUserHasSetPassword(true)
        //   setShowInvestedUserModal(true)
        // } else {
        //   const res = await sendSetPasswordEmail(props.getValues('email'))
        //   console.log('Set Password Email sent!', res)
        //   setInvestedDescription('Kindly check your email for the set password link')
        //   setUserHasSetPassword(false)
        //   setShowInvestedUserModal(true)
        // }
        // props.reset()
      } else if (emailStatus === EMAIL_ONBOARDING_STATUS.IS_CREATED_FROM_DIRECT_INVEST) {
        // TODO: Add handling for users from direct invest
      }
    } catch (error: any) {
      consoleLog('Onboarding URL error', error)
      setisLoading(false)
    }
  }

  const sendOnboardingURLToEmail = async () => {
    setshowExistingEmailModal(false)
    setisLoading(true)

    const sendOnboardingURLRes = await sendOnboardingURL(props.getValues('email'))

    if (sendOnboardingURLRes.status === 201) {
      setisLoading(false)
      setshowEmailConfirmationSentModal(true)
    }
    consoleLog('Onboarding URL response', sendOnboardingURLRes)
  }

  const isSubmitDisabled = () => {
    const formValues = props.getValues(['email', 'basicInfo.firstName', 'basicInfo.lastName'])

    if (formValues.includes('')) {
      return true
    }
    return false
  }

  const fetchData = async (onboardingId: string, regulation: string) => {
    setisLoading(true)
    try {
      const getOnboardProgressRes = await getOnboardProgress(onboardingId)
      if (getOnboardProgressRes.status === 200) {
        const data = getOnboardProgressRes.data.userOnboarding
        if (data !== undefined) {
          props.setValue('_id', data._id)
          props.setValue('email', data.email)
          props.setValue('onboardingId', data.onboardingId)
          props.setValue('regulation', regulation)
          for (const key in data.capturedData) {
            props.setValue(key, data.capturedData[key])
          }

          if (String(getOnboardProgressRes.data.userOnboarding.lastCompletedStage) === '10') {
            setEmail(data.email)
            setInvestedDescription('You can now reinvest by clicking Continue')
            setShowInvestedUserModal(true)
          } else {
            continueToLastStop(String(getOnboardProgressRes.data.userOnboarding.lastCompletedStage))
          }
        } else {
          const repeatedData = getOnboardProgressRes?.data
          consoleLog('Repeated user info', repeatedData)
          if (repeatedData.isFinished) {
            setShowInvestedUserModal(true)
            props.reset()
          }
        }
      }
    } catch (error) {
      setisLoading(false)
      setShowExpiredInvestorLink(true)
      consoleLog('Email fetch form error', error)
      ReactGA.event({
        category: 'Email page',
        action: `Email page error`,
        label: JSON.stringify(error),
      })
    }
  }

  useEffect(() => {
    const queryParams = queryString.parse(location.search)
    const onboardingId = queryParams.onboardingId
    const regulation = queryParams.regulation

    props.setValue('regulation', regulation)

    sendDataToGTM({
      event: 'funnel_step',
      step_title: 'EMAIL',
      ...gtmData,
    })

    if (onboardingId) {
      props.setValue('onboardingId', onboardingId)
      fetchData(onboardingId as string, regulation as string)
    } else if (props.getValues('fromSecondPage')) {
      consoleLog('retain state')
    } else {
      props.reset()
    }
  }, [location.search]) // eslint-disable-line

  return (
    <div className="flex h-screen w-full flex-col md:flex-row md:justify-start">
      <div className="mt-10 flex flex-col sm:w-screen md:mt-0 md:min-h-screen md:w-[45%] lg:w-[40%] xl:w-[35%]">
        <div className="relative w-screen px-10 sm:h-auto sm:px-16 md:hidden">
          <img className="w-screen" src={CompanyLogo} alt="CompanyLogo" />
        </div>
        <div className="relative hidden flex-grow md:flex">
          <img className="flex h-screen w-full" src={Side1} alt="Side1" />
        </div>
      </div>

      <div className="flex flex-col p-10 sm:w-[100%] md:w-[55%] lg:h-screen lg:w-[60%] lg:overflow-y-scroll xl:w-[65%]">
        <div className="md:my-3">
          <Stepper current={props.currentStage} />
        </div>

        <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-center md:text-4xl">
          Invest in Armed Forces Brewing Co.
        </div>

        <div>
          <div>
            <FormLabel labelName={'Email Address'} />
            <InputField
              registerField={props.registerField}
              fieldId={'email'}
              type={'email'}
              required={true}
              validate={{
                validEmail: (value: string) => /^[a-zA-Z0-9._+\]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value),
                validEmail2: (value: string) => /^[a-zA-Z0-9._+\]+@[a-zA-Z0-9.-]+(?<!\.con)$/.test(value),
              }}
              placeholder={'sample@email.com'}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                props.clearErrors('email')
                props.setValue('email', e.target.value)
              }}
            />
            {(props.errors.email?.type === 'validEmail' || props.errors.email?.type === 'validEmail2') && (
              <ErrorMessage message={'Not a valid email.'} />
            )}
          </div>

          <div className="w-full lg:flex">
            <div className="w-full py-1 lg:mr-4 lg:py-0">
              <FormLabel labelName={'Legal First Name'} />
              <InputField
                registerField={props.registerField}
                fieldId={'basicInfo.firstName'}
                required={true}
                validate={{
                  minLength: (value: string) => value.length > 1,
                  validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                }}
                placeholder={'First Name'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('basicInfo.firstName')
                  props.setValue('basicInfo.firstName', e.target.value)
                }}
              />
              {props.errors.basicInfo?.firstName?.type === 'minLength' && (
                <ErrorMessage message={'Minimum of two characters.'} />
              )}
              {props.errors.basicInfo?.firstName?.type === 'validName' && (
                <ErrorMessage message={'Not a valid name.'} />
              )}
              {props.errors.basicInfo?.firstName?.type === 'required' && (
                <ErrorMessage message={'Legal first name is required'} />
              )}
            </div>

            <div className="w-full py-1 lg:py-0">
              <FormLabel labelName={'Legal Last Name'} />
              <InputField
                registerField={props.registerField}
                fieldId={'basicInfo.lastName'}
                required={true}
                validate={{
                  minLength: (value: string) => value.length > 1,
                  validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                }}
                placeholder={'Last Name'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('basicInfo.lastName')
                  props.setValue('basicInfo.lastName', e.target.value)
                }}
              />
              {props.errors.basicInfo?.lastName?.type === 'minLength' && (
                <ErrorMessage message={'Minimum of two characters.'} />
              )}
              {props.errors.basicInfo?.lastName?.type === 'validName' && <ErrorMessage message={'Not a valid name.'} />}
              {props.errors.basicInfo?.lastName?.type === 'required' && (
                <ErrorMessage message={'Legal last Name is required'} />
              )}
            </div>
          </div>

          <div className="w-full justify-center gap-3 py-10 sm:block lg:flex">
            <div className="flex flex-col sm:w-[100%] lg:w-[100%]">
              <Button
                btnName={'Next: Select How Your Investment Will Be Held'}
                btnType={'button'}
                disabled={isSubmitDisabled()}
                onClickHandler={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
      <LoaderModal isVisible={isLoading} />
      <MessageModal
        isVisible={showEmailVerifiedModal}
        onProceed={() => {
          props.calcProgressState(lastCompletedStage)
          props.setFormState({ step: lastCompletedStage })
        }}
        onCancel={() => {
          setshowEmailVerifiedModal(false)
        }}
        title={'Email is verified'}
        description={'Proceed to where you last stopped.'}
        type={'success'}
        btn2={'Proceed'}
      />
      <InvestedEmailModal
        isVisible={showInvestedUserModal}
        onReinvest={() => {
          setisLoading(false)
          setShowInvestedUserModal(false)
          setisLoading(false)
          navigate('/reinvest?regulation=reg-a')
        }}
        onStartBeginning={() => {
          props.reset()
          setShowInvestedUserModal(false)
          setisLoading(false)
        }}
        onCancel={() => {
          setisLoading(false)
          setShowInvestedUserModal(false)
          setisLoading(false)
        }}
      />
      <MessageModal
        isVisible={showEmailConfirmationSentModal}
        onProceed={() => {
          setshowEmailConfirmationSentModal(false)
        }}
        onCancel={() => {
          setshowEmailConfirmationSentModal(false)
        }}
        title={'We recognize your email address'}
        description={'Please check your email to continue filling out the form.'}
        type={'success'}
        btn2={'Ok'}
      />
      <ExistingEmailModal
        isVisible={showExistingEmailModal}
        onStartBeginning={onStartFromBeginning}
        onStartFromLastAccessedPage={() => {
          sendOnboardingURLToEmail()
        }}
        onCancel={() => {
          setshowExistingEmailModal(false)
          setisLoading(false)
        }}
      />
      <MessageModal
        isVisible={showExpiredInvestorLink}
        onProceed={() => {
          setShowExpiredInvestorLink(false)
        }}
        onCancel={() => {
          setShowExpiredInvestorLink(false)
        }}
        title={'Error'}
        description={'You have an expired investor form link. Please reach out to your broker dealer.'}
        type={'error'}
        btn2={'Ok'}
      />
    </div>
  )
}

export default Email
